/**
 * Structural view of `Document` / `ShadowRoot` (`activeElement`) and of
 * `Element` (`shadowRoot`): the walk below needs nothing else, so it can be
 * tested without a DOM.
 */
export interface IFocusRoot<T> {
  readonly activeElement: T | null;
}

export interface IShadowHost<T> {
  readonly shadowRoot: IFocusRoot<T> | null;
}

/**
 * The focused element of `root`, following open shadow roots down to the
 * innermost one. `document.activeElement` alone stops at the shadow host
 * (e.g. `<gpt-help>`), so a focused field inside a custom element looks
 * "not editable" to the keyboard-resize guards.
 */
export function deepActiveElementOf<T extends IShadowHost<T>>(
  root: IFocusRoot<T>
): T | null {
  let active = root.activeElement;
  while (active) {
    const inner = active.shadowRoot?.activeElement ?? null;
    if (!inner) break;
    active = inner;
  }
  return active;
}

/** The innermost focused element of the page (null outside a browser). */
export function deepActiveElement(): Element | null {
  if (typeof document === 'undefined') return null;
  return deepActiveElementOf<Element>(document);
}
